import { LoadingSkeleton } from "@components/skeleton-loading";
import {
  CustomerNotificationDTO,
  CustomerNotificationStatusEnum,
  CustomerNotificationStatusTypes,
} from "@hooks/dtos/ICustomerNotificationDTO";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import {
  getNotificationHideActionStatus,
  getNotificationProperty,
  getNotificationReadActionStatus,
  getNotificationStatusProperty,
  changeNotificationStatus,
} from "@hooks/functions/Notification/Notification";
import { api } from "@service/api";
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import dayjs from "dayjs";

// ---------------------------------------------------------------------------
// Template interpolation
// Replaces every {{key}} token in templateHtml with the matching value from
// metadata.fields. Unknown keys are wrapped in <mark> for dev visibility.
// ---------------------------------------------------------------------------
function interpolateTemplate(
  templateHtml: string,
  metadata: Record<string, any>,
): string {
  const fields: Record<string, string> = metadata?.fields ?? {};
  return templateHtml.replace(/\{\{(\w+)\}\}/g, (_, key) =>
    key in fields ? String(fields[key]) : `<mark>{{${key}}}</mark>`,
  );
}

// ---------------------------------------------------------------------------
// Reusable meta cell used in the info strip below the header
// ---------------------------------------------------------------------------
function MetaCell({
  label,
  value,
  borderEnd = true,
}: {
  label: string;
  value: string;
  borderEnd?: boolean;
}) {
  return (
    <div
      className={`d-flex flex-column flex-fill px-4 py-2${borderEnd ? " border-end" : ""}`}
      style={{ gap: 2 }}
    >
      <span
        style={{
          fontSize: "0.6rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          color: "#a0aec0",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: "0.8rem",
          fontWeight: 700,
          color: "#2d3748",
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface IProps {
  notificationId: number;
  isOpen: boolean;
  onClose: (updated?: boolean) => void;
  /** Optional: called after any status mutation so the parent list can refresh */
  onStatusChanged?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function ModalNotificationView({
  notificationId,
  isOpen,
  onClose,
  onStatusChanged,
}: IProps) {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [animate, setAnimate] = useState(false);
  const [updated, setUpdated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState<
    CustomerNotificationDTO | undefined
  >();

  // -------------------------------------------------------------------------
  // Fetch notification detail
  // -------------------------------------------------------------------------
  const getNotificationInfo = async () => {
    try {
      setIsLoading(true);
      const result = await api.get(
        `/notification?notificationId=${notificationId}`,
      );
      setNotification(result.data);
    } catch (err: any) {
      pushToastify({
        message: err?.response?.data.message ?? "Erro ao buscar notificação!",
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Status mutation with SweetAlert confirmation gate
  // -------------------------------------------------------------------------
  const handleChangeStatus = (
    status: CustomerNotificationStatusTypes,
    confirmText: string,
  ) => {
    if (!notification) return;
    Swal.fire({
      title: "Alterar Status?",
      text: confirmText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sim, continuar!",
      cancelButtonText: "Cancelar",
    }).then(async (res: { isConfirmed: boolean }) => {
      if (!res.isConfirmed) return;
      try {
        await changeNotificationStatus({
          notificationId: notification.id,
          status,
        });
        await getNotificationInfo();
        onStatusChanged?.();
      } catch (err: any) {
        pushToastify({
          message: err?.message ?? "Ocorreu um erro!",
          type: "error",
          theme: "dark",
        });
      }
    });
  };

  // -------------------------------------------------------------------------
  // Modal open / close / ESC key lifecycle
  // -------------------------------------------------------------------------
  function handleCancel() {
    setAnimate(false);
    setTimeout(() => onClose(updated), 300);
  }

  const handleNotificationAsRead = async (notificationId: number) => {
    try {
      await changeNotificationStatus({
        notificationId,
        status: CustomerNotificationStatusEnum.READ,
      });
      setUpdated(true);
    } catch (err: any) {
      pushToastify({
        message: err.message || "Ocorreu um erro!",
        type: "error",
        theme: "dark",
      });
    }
  };

  useEffect(() => {
    if (
      notification &&
      notification.status === CustomerNotificationStatusEnum.UNREAD
    ) {
      handleNotificationAsRead(notification.id);
    }
  }, [notification]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleCancel();
    };
    if (isOpen) {
      getNotificationInfo();
      setTimeout(() => setAnimate(true), 10);
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    } else {
      setAnimate(false);
    }
  }, [isOpen]);

  // -------------------------------------------------------------------------
  // Derived display values
  // -------------------------------------------------------------------------
  const notifProp = notification
    ? getNotificationProperty({
        subCategoryId: notification.subCategoryData.id,
        groupKey: notification.subCategoryData.groupKey,
      })
    : null;

  const statusProp = notification
    ? getNotificationStatusProperty({ status: notification.status })
    : null;

  const readAction = notification
    ? getNotificationReadActionStatus({ status: notification.status })
    : null;

  const hideAction = notification
    ? getNotificationHideActionStatus({ status: notification.status })
    : null;

  const renderedHtml = notification?.subCategoryData?.templateHtml
    ? interpolateTemplate(
        notification.subCategoryData.templateHtml,
        notification.metadata ?? {},
      )
    : "";

  if (!isOpen) return null;

  return (
    <>
      {/* ------------------------------------------------------------------ */}
      {/* Modal                                                               */}
      {/* ------------------------------------------------------------------ */}
      <div
        className={`modal fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        aria-labelledby="nvModalTitle"
        aria-modal="true"
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
          <div
            className="modal-content border-0 overflow-hidden"
            style={{
              borderRadius: 18,
              boxShadow:
                "0 24px 72px rgba(0,0,0,0.28), 0 4px 16px rgba(0,0,0,0.14)",
            }}
          >
            {/* -------------------------------------------------------------- */}
            {/* HEADER — dark gradient, icon + title on one line, status below  */}
            {/* -------------------------------------------------------------- */}
            <div
              className="modal-header border-0 p-0"
              style={{
                background:
                  "linear-gradient(150deg, #0c1322 0%, #162038 55%, #0d1a30 100%)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Decorative ambient glow blobs */}
              <span
                style={{
                  position: "absolute",
                  width: 280,
                  height: 280,
                  top: -90,
                  right: -70,
                  borderRadius: "50%",
                  pointerEvents: "none",
                  background:
                    "radial-gradient(circle, rgba(96,165,250,0.13) 0%, transparent 68%)",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  width: 200,
                  height: 200,
                  bottom: -70,
                  left: 10,
                  borderRadius: "50%",
                  pointerEvents: "none",
                  background:
                    "radial-gradient(circle, rgba(167,139,250,0.09) 0%, transparent 65%)",
                }}
              />

              <div
                className="d-flex align-items-center gap-3 w-100 p-4"
                style={{ position: "relative", zIndex: 1 }}
              >
                {/* Category icon */}
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: 14,
                    backgroundColor: notifProp?.bgColor ?? "#1e3a5f",
                    boxShadow:
                      "0 6px 20px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.15)",
                  }}
                >
                  {notifProp && (
                    <notifProp.icon
                      style={{ color: notifProp.textColor, fontSize: 21 }}
                    />
                  )}
                </div>

                {/* Title + status badge on same line, subcategory title below */}
                <div className="flex-grow-1 overflow-hidden">
                  {/* Row 1: title + status badge right-aligned */}
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <h5
                      id="nvModalTitle"
                      className="mb-0 text-truncate"
                      style={{
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: "#eef2ff",
                        letterSpacing: "-0.01em",
                        lineHeight: 1.3,
                      }}
                    >
                      {notification?.subCategoryData?.title ?? "Notificação"}
                    </h5>

                    {/* Status pill — solid bg ensures visibility on any dark bg */}
                    {statusProp && (
                      <span
                        className="badge flex-shrink-0"
                        style={{
                          fontSize: "0.73rem",
                          fontWeight: 700,
                          padding: "5px 12px",
                          borderRadius: 99,
                          background: statusProp.bgColor,
                          color: "#fff",
                          border: "2px solid rgba(255,255,255,0.2)",
                          textShadow: "0 1px 3px rgba(0,0,0,0.5)",
                        }}
                        title={statusProp.description}
                      >
                        {statusProp.text}
                      </span>
                    )}
                  </div>

                  {/* Row 2: category › group — subtle breadcrumb */}
                  <div
                    className="d-flex align-items-center gap-1 mt-2"
                    style={{
                      fontSize: "1rem",
                      color: "rgba(255,255,255,0.45)",
                    }}
                  >
                    {notification?.categoryData?.title && (
                      <span
                        style={{ color: "#93c5fd", fontWeight: 500 }}
                        title={notification?.categoryData?.description}
                      >
                        {notification.categoryData.title}
                      </span>
                    )}
                    {notification?.subCategoryData?.groupLabel && (
                      <>
                        <span>›</span>
                        <span
                          style={{ color: "#c4b5fd", fontWeight: 500 }}
                          title={notification.subCategoryData.groupLabel}
                        >
                          {notification.subCategoryData.groupLabel}
                        </span>
                      </>
                    )}
                    {notification?.subCategoryData?.title && (
                      <>
                        <span>›</span>
                        <span
                          className="text-white"
                          style={{ fontWeight: 500 }}
                          title={notification?.subCategoryData?.description}
                        >
                          {notification?.subCategoryData?.title}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Close button */}
                <button
                  type="button"
                  className="btn-close btn-close-white flex-shrink-0"
                  style={{ opacity: 0.55 }}
                  onClick={handleCancel}
                  aria-label="Fechar"
                />
              </div>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* META STRIP — ID first, then dates                               */}
            {/* -------------------------------------------------------------- */}
            {!isLoading && notification && (
              <div
                className="d-flex border-bottom"
                style={{ background: "#f7f8fc" }}
              >
                <MetaCell label="ID" value={`#${notification.id}`} />
                <MetaCell
                  label="Recebida em"
                  value={dayjs(notification.createdAt).format(
                    "DD/MM/YYYY [às] HH:mm",
                  )}
                />
                {notification.actionAt && (
                  <MetaCell
                    label="Última ação"
                    value={dayjs(notification.actionAt).format(
                      "DD/MM/YYYY [às] HH:mm",
                    )}
                    borderEnd={false}
                  />
                )}
              </div>
            )}

            {/* -------------------------------------------------------------- */}
            {/* BODY                                                            */}
            {/* -------------------------------------------------------------- */}
            <div className="modal-body px-4 py-3">
              {isLoading ? (
                <LoadingSkeleton count={4} />
              ) : notification ? (
                <>
                  {/* -------------------------------------------------------- */}
                  {/* Interpolated HTML template                               */}
                  {/* -------------------------------------------------------- */}
                  {renderedHtml ? (
                    <div
                      style={{
                        fontSize: "0.88rem",
                        lineHeight: 1.75,
                        color: "#2d3748",
                        background: "#fff",
                        border: "1px solid #e8ecf5",
                        borderRadius: 12,
                        padding: "20px 22px",
                        boxShadow:
                          "0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.04)",
                      }}
                      dangerouslySetInnerHTML={{ __html: renderedHtml }}
                    />
                  ) : (
                    <p
                      className="text-muted text-center py-4"
                      style={{ fontSize: "0.85rem" }}
                    >
                      Sem conteúdo disponível para esta notificação.
                    </p>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <div style={{ fontSize: "2.5rem", opacity: 0.35 }}>🔔</div>
                  <p
                    className="text-muted mt-2"
                    style={{ fontSize: "0.88rem" }}
                  >
                    Nenhuma informação encontrada.
                  </p>
                </div>
              )}
            </div>

            {/* -------------------------------------------------------------- */}
            {/* FOOTER — action buttons                                         */}
            {/* -------------------------------------------------------------- */}
            <div
              className="modal-footer border-top d-flex justify-content-between flex-wrap gap-2"
              style={{ background: "#f7f8fc", padding: "12px 24px 16px" }}
            >
              {/* Left: read/unread + hide/show */}
              <div className="d-flex gap-2 flex-wrap">
                {/* Read / Unread toggle */}
                {readAction && (
                  <button
                    type="button"
                    className="btn btn-sm d-flex align-items-center gap-2"
                    style={{
                      backgroundColor: readAction.bgColor,
                      color: readAction.textColor,
                      border: "none",
                      borderRadius: 10,
                      fontWeight: 600,
                      fontSize: "0.82rem",
                      padding: "8px 16px",
                    }}
                    title={readAction.text}
                    onClick={() =>
                      handleChangeStatus(readAction.newStatus, readAction.text)
                    }
                  >
                    <readAction.icon style={{ fontSize: "0.95rem" }} />
                    {readAction.text}
                  </button>
                )}

                {/* Hide / Show toggle */}
                {hideAction && (
                  <button
                    type="button"
                    className="btn btn-sm d-flex align-items-center gap-2"
                    style={{
                      backgroundColor: (hideAction as any).bgColor ?? "#4a5568",
                      color: (hideAction as any).textColor ?? "#fff",
                      border: "none",
                      borderRadius: 10,
                      fontWeight: 600,
                      fontSize: "0.82rem",
                      padding: "8px 16px",
                    }}
                    title={(hideAction as any).text}
                    onClick={() =>
                      handleChangeStatus(
                        (hideAction as any).newStatus,
                        (hideAction as any).text,
                      )
                    }
                  >
                    <hideAction.icon style={{ fontSize: "0.95rem" }} />
                    {(hideAction as any).text}
                  </button>
                )}
              </div>

              {/* Right: dismiss */}
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                style={{
                  borderRadius: 10,
                  fontWeight: 500,
                  padding: "8px 20px",
                  fontSize: "0.82rem",
                }}
                onClick={handleCancel}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop */}
      <div
        className={`modal-backdrop fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        onClick={handleCancel}
      />
    </>
  );
}
